import { Link, useInRouterContext } from "react-router-dom";
import { useTranslation } from "react-i18next";

// ---------------------------------------------------------------------------
// Ticket ligado a una entrada de tiempo (#1303). Abre el tablero de
// Actividades con `?ticket=<id>`; las entradas anteriores a #1303 no traen
// ticket y lo dicen («Sin ticket») en vez de dejar la celda vacía.
// ---------------------------------------------------------------------------

/** Ruta del tablero Kanban de Gestión de Staff; `ticket` abre el detalle. */
export const activityBoardPath = (activityId: string): string => `/gestion-staff/actividades/actividades?ticket=${encodeURIComponent(activityId)}`;

interface Props {
  activityId?: string | null;
  activityKey?: string | null;
  activityTitle?: string | null;
  /** Clases del texto secundario del tema. */
  secondaryClass?: string;
  /** `inline` = chip pequeño (bajo la nota); `cell` = celda de tabla. */
  variant?: "cell" | "inline";
  testId?: string;
}

export default function ActivityLink({ activityId, activityKey, activityTitle, secondaryClass = "", variant = "cell", testId }: Props) {
  const { t } = useTranslation("common");
  // Fuera de un router (pruebas, vistas incrustadas) se cae a un enlace normal.
  const inRouter = useInRouterContext();
  if (!activityId) {
    return (
      <span className={`rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[11px] text-amber-600 ${variant === "inline" ? "ml-1" : ""}`} data-testid={testId}>
        {t("workHours.timeEntries.noActivity")}
      </span>
    );
  }
  const label = activityKey || activityId;
  const className = `${variant === "inline" ? "ml-1 " : ""}inline-flex max-w-full items-center gap-1 text-[11px] text-violet-500 hover:underline`;
  const inner = (
    <>
      <i className="ri-ticket-line" />
      <span className="font-mono">{label}</span>
      {variant === "cell" && activityTitle && <span className={`truncate ${secondaryClass}`}>· {activityTitle}</span>}
    </>
  );
  if (!inRouter) {
    return (
      <a href={activityBoardPath(activityId)} title={activityTitle || label} className={className} data-testid={testId}>
        {inner}
      </a>
    );
  }
  return (
    <Link
      to={activityBoardPath(activityId)}
      title={activityTitle || label}
      className={className}
      data-testid={testId}
    >
      {inner}
    </Link>
  );
}
